import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type LandingLang = 'en' | 'id';
/** A string in both languages. */
export type L10n = Record<LandingLang, string>;

const LANG_KEY = 'hv-landing-lang';

/** ?lang= wins, then the visitor's last choice, then the browser language. */
function initialLang(param: string | null): LandingLang {
    if (param === 'en' || param === 'id') return param;
    try {
        const saved = localStorage.getItem(LANG_KEY);
        if (saved === 'en' || saved === 'id') return saved;
    } catch { /* storage unavailable */ }
    return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('id') ? 'id' : 'en';
}

/**
 * Language of the bilingual landing pages (/faq, /docs). The choice is kept in
 * the URL (shareable) and in localStorage (remembered across pages).
 */
export function useLandingLang(): [LandingLang, (next: LandingLang) => void] {
    const [params, setParams] = useSearchParams();
    const [lang, setLangState] = useState<LandingLang>(() => initialLang(params.get('lang')));

    const setLang = (next: LandingLang) => {
        setLangState(next);
        try { localStorage.setItem(LANG_KEY, next); } catch { /* storage unavailable */ }
        const p = new URLSearchParams(params);
        p.set('lang', next);
        setParams(p, { replace: true, preventScrollReset: true });
    };

    return [lang, setLang];
}
